import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, ArrowLeft, Calendar, Clock, Video, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const BookConsultation = () => {
  const navigate = useNavigate();

  useEffect(() => {
    // Load Calendly widget script
    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0a0e1a] via-[#0d1525] to-[#0a0e1a] text-white">
      {/* Header */}
      <nav className="fixed top-0 w-full bg-[#0a0e1a]/95 backdrop-blur-md border-b border-cyan-500/10 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center space-x-3 cursor-pointer" onClick={() => navigate('/')}>
              <div className="relative">
                <img src="/logo.png" alt="Lexprive Consultants" className="h-14 w-14 object-contain" />
                <div className="absolute inset-0 blur-lg bg-cyan-400/20 -z-10"></div>
              </div>
              <div>
                <span className="text-xl font-bold bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">
                  Lexprive Consultants LLP
                </span>
                <p className="text-xs text-gray-400">Securing Your Digital Future with Expert Data Protection Solutions</p>
              </div>
            </div>
            
            <Button 
              onClick={() => navigate('/')}
              variant="outline"
              className="border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Home
            </Button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header Section */}
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-gradient-to-r from-cyan-500/20 to-purple-500/20 text-cyan-400 border-cyan-500/30 px-4 py-2">
              Book Your Consultation
            </Badge>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-white via-cyan-200 to-white bg-clip-text text-transparent">
              Schedule Your DPDPA Compliance Consultation
            </h1>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Connect with our data protection experts via Google Meet. Choose your preferred date and time slot between 10 AM - 6 PM.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Left Column - Benefits */}
            <div className="lg:col-span-1 space-y-6">
              <Card className="bg-gradient-to-br from-[#1a1f35] to-[#0d1525] border-cyan-500/20">
                <CardHeader>
                  <CardTitle className="text-white">What to Expect</CardTitle>
                  <CardDescription className="text-gray-400">
                    Your consultation will include
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-white font-medium">Initial Assessment</p>
                      <p className="text-sm text-gray-400">Understanding your compliance needs and challenges</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-white font-medium">Expert Guidance</p>
                      <p className="text-sm text-gray-400">Personalized advice on DPDPA compliance strategy</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-white font-medium">Roadmap Discussion</p>
                      <p className="text-sm text-gray-400">Overview of implementation timeline and approach</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-white font-medium">Q&A Session</p>
                      <p className="text-sm text-gray-400">Address your specific questions and concerns</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-[#1a1f35] to-[#0d1525] border-cyan-500/20">
                <CardHeader>
                  <CardTitle className="text-white">Consultation Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 bg-gradient-to-br from-cyan-500/20 to-purple-500/20 rounded-lg flex items-center justify-center">
                      <Clock className="h-5 w-5 text-cyan-400" />
                    </div>
                    <div>
                      <p className="text-white font-medium">Duration</p>
                      <p className="text-sm text-gray-400">30-45 minutes</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 bg-gradient-to-br from-cyan-500/20 to-purple-500/20 rounded-lg flex items-center justify-center">
                      <Video className="h-5 w-5 text-cyan-400" />
                    </div>
                    <div>
                      <p className="text-white font-medium">Platform</p>
                      <p className="text-sm text-gray-400">Google Meet (link sent via email)</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 bg-gradient-to-br from-cyan-500/20 to-purple-500/20 rounded-lg flex items-center justify-center">
                      <Calendar className="h-5 w-5 text-cyan-400" />
                    </div>
                    <div>
                      <p className="text-white font-medium">Availability</p>
                      <p className="text-sm text-gray-400">10 AM - 6 PM (Mon-Sat)</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="bg-gradient-to-br from-cyan-500/10 to-purple-500/10 border border-cyan-500/20 rounded-xl p-6">
                <p className="text-sm text-gray-300">
                  <strong className="text-cyan-400">Note:</strong> After booking, you'll receive an email confirmation with the Google Meet link and calendar invite. You can reschedule or cancel anytime.
                </p>
              </div>
            </div>

            {/* Right Column - Calendly Embed */}
            <div className="lg:col-span-2">
              <Card className="bg-gradient-to-br from-[#1a1f35] to-[#0d1525] border-cyan-500/20 overflow-hidden">
                <CardHeader>
                  <CardTitle className="text-white">Select Your Preferred Time</CardTitle>
                  <CardDescription className="text-gray-400">
                    Choose a date and time that works best for you
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-0">
                  {/* Calendly Inline Widget */}
                  <div 
                    className="calendly-inline-widget" 
                    data-url="https://calendly.com/your-calendly-link?primary_color=00d4ff&background_color=0d1525&text_color=ffffff"
                    style={{ minWidth: '320px', height: '700px' }}
                  ></div>
                  
                  {/* Placeholder until you add Calendly link */}
                  <div className="p-8 text-center bg-[#0d1525] min-h-[700px] flex flex-col items-center justify-center">
                    <Calendar className="h-20 w-20 text-cyan-400 mb-6" />
                    <h3 className="text-2xl font-bold text-white mb-4">Calendly Integration</h3>
                    <p className="text-gray-400 mb-6 max-w-md">
                      To enable online booking, please update the Calendly URL in the code with your Calendly booking link.
                    </p>
                    <div className="bg-gradient-to-r from-cyan-500/10 to-purple-500/10 border border-cyan-500/20 rounded-lg p-4 max-w-2xl">
                      <p className="text-sm text-gray-300 text-left">
                        <strong className="text-cyan-400">Instructions:</strong><br/>
                        1. Create a free account at <a href="https://calendly.com" target="_blank" rel="noopener noreferrer" className="text-cyan-400 underline">calendly.com</a><br/>
                        2. Set up your availability (10 AM - 6 PM)<br/>
                        3. Enable Google Meet integration<br/>
                        4. Copy your Calendly link<br/>
                        5. Replace the placeholder URL in <code className="bg-[#0a0e1a] px-2 py-1 rounded text-cyan-400">BookConsultation.jsx</code><br/>
                        6. Set notification email: hasnainalvi.hnlu@gmail.com
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookConsultation;
